import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { extractErrorMessage } from '@/lib/api-errors';

/**
 * ImageUpload Component
 * 
 * Allows users to either:
 * 1. Upload an image file directly
 * 2. Enter an external image URL
 * 
 * Props:
 * - value: Current image URL
 * - onChange: Callback when image URL changes
 * - label: Label text (default: "Imagem")
 * - accept: Accepted file types (default: "image/*")
 * - maxSize: Max file size in MB (default: 5)
 * - aspectRatio: Display aspect ratio (default: "aspect-video")
 * - recommendedSize: Hint shown near the upload zone (e.g. "1600×900 px · 16:9 · até 250 KB")
 * - hint: Extra free-form tip (small muted text)
 */
export default function ImageUpload({ 
  value = '', 
  onChange, 
  label = 'Imagem',
  accept = 'image/jpeg,image/png,image/gif,image/webp',
  maxSize = 5,
  aspectRatio = 'aspect-video',
  recommendedSize = '',
  hint = '',
  className = ''
}) {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState(value && !value.startsWith('/api/uploads') ? 'url' : 'upload');
  const [urlInput, setUrlInput] = useState(value && !value.startsWith('/api/uploads') ? value : '');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`Ficheiro demasiado grande. Máximo: ${maxSize}MB`);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, seleciona uma imagem');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        onChange(response.data.url);
        toast.success('Imagem carregada com sucesso!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const message = extractErrorMessage(error, 'Erro ao carregar imagem');
      toast.error(message);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) {
      toast.error('Por favor, introduza um URL válido');
      return;
    }

    // Basic URL validation
    try {
      new URL(urlInput);
      onChange(urlInput);
      toast.success('URL da imagem guardado');
    } catch {
      toast.error('URL inválido');
    }
  };

  const handleRemove = () => {
    onChange('');
    setUrlInput('');
  };

  // Use the value as-is. If it's a `/api/...` upload path it resolves
  // relative to the current origin; if it's already an absolute URL it
  // works as-is. This avoids cross-origin TLS/CORS failures when the bundle
  // was built with REACT_APP_BACKEND_URL pointing to a different host.
  const currentImageUrl = value || null;

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label>{label}</Label>
        {recommendedSize && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-ruby/10 border border-ruby/30 px-2.5 py-0.5 text-[11px] font-medium text-ruby"
            title="Tamanho recomendado"
            data-testid="upload-size-hint"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="9" cy="9" r="1.5"/>
              <path d="m21 15-5-5L5 21"/>
            </svg>
            {recommendedSize}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground -mt-1">{hint}</p>}
      
      {/* Mode Toggle */}
      <div className="flex gap-2 mb-3">
        <Button
          type="button"
          variant={mode === 'upload' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('upload')}
          className={mode === 'upload' ? 'bg-ruby hover:bg-ruby-light' : ''}
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload
        </Button>
        <Button
          type="button"
          variant={mode === 'url' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('url')}
          className={mode === 'url' ? 'bg-ruby hover:bg-ruby-light' : ''}
        >
          <ImageIcon className="w-4 h-4 mr-2" />
          URL
        </Button>
      </div>

      {/* Preview */}
      {currentImageUrl && (
        <div className={`relative ${aspectRatio} bg-surface rounded-lg overflow-hidden border border-border`}>
          <img 
            src={currentImageUrl} 
            alt="Preview" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23333" width="100" height="100"/><text fill="%23666" x="50" y="55" text-anchor="middle" font-size="12">Erro</text></svg>';
            }}
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            onClick={handleRemove}
            className="absolute top-2 right-2 w-8 h-8 rounded-full"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Upload Mode */}
      {mode === 'upload' && !currentImageUrl && (
        <div 
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`
            ${aspectRatio} border-2 border-dashed border-border rounded-lg 
            flex flex-col items-center justify-center gap-2 
            cursor-pointer hover:border-ruby/50 hover:bg-ruby/5 
            transition-colors
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 text-ruby animate-spin" />
              <p className="text-sm text-muted-foreground">A carregar...</p>
            </>
          ) : (
            <>
              <Upload className="w-8 h-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Clica para selecionar</p>
              <p className="text-xs text-muted-foreground">JPG, PNG, GIF ou WebP (máx. {maxSize}MB)</p>
              {recommendedSize && (
                <p className="text-xs text-ruby/80 font-medium">Recomendado: {recommendedSize}</p>
              )}
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
        </div>
      )}

      {/* URL Mode */}
      {mode === 'url' && !currentImageUrl && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder="https://exemplo.com/imagem.jpg"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              className="flex-1 bg-surface border-border"
            />
            <Button
              type="button"
              onClick={handleUrlSubmit}
              className="bg-ruby hover:bg-ruby-light"
            >
              Guardar
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Introduza o URL completo de uma imagem externa
          </p>
        </div>
      )}

      {/* Hidden input for form submission */}
      <input type="hidden" value={value} />
    </div>
  );
}
